import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import userStore from "../stores/userStore";
import {
  clearLoggedOut,
  getLastPage,
  HOME_PATH,
  LOGOUT_BROADCAST_KEY,
  SESSION_STORAGE_KEY,
  takeRedirectAfterLogin,
} from "./storage";

const parseSession = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error("Failed to read the session saved by another tab", error);
    return null;
  }
};

// Keeps all open tabs of the portal on the same session. Each tab holds the user in memory,
// so without this, logging in again in one tab (e.g. after the session expired) left the
// other tabs stuck on the login screen. The browser's `storage` event fires in every
// *other* tab when localStorage changes, so a tab never reacts to its own writes.
export const useSessionSync = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  useEffect(() => {
    const handleLogin = (session) => {
      const wasLoggedOut = !userStore.isAuthenticated;
      userStore.setError("");
      userStore.setUserDetails(session.userDetails);
      userStore.setIsCoralisEmployee(!!session.isCoralisEmployee);
      userStore.setIsAuthenticated(true);

      if (wasLoggedOut) {
        clearLoggedOut();
        // A tab parked on the login screen goes back to its own last page (saved per tab)
        if (pathnameRef.current.startsWith("/login")) {
          navigate(
            takeRedirectAfterLogin() || getLastPage(session.userDetails.USERNAME) || HOME_PATH,
            { replace: true }
          );
        }
      }
    };

    const handleStorage = (e) => {
      if (e.storageArea !== window.localStorage) return;

      if (e.key === LOGOUT_BROADCAST_KEY) {
        if (userStore.isAuthenticated) {
          userStore.logout({ broadcast: false });
          navigate("/login", { replace: true });
        }
        return;
      }

      if (e.key !== SESSION_STORAGE_KEY || !e.newValue) return;

      const session = parseSession(e.newValue);
      const accessToken = session?.userDetails?.accessToken;
      if (!accessToken) return;

      // A full login in another tab. Skipped while that tab is in the forced password change,
      // which must finish there first.
      if (session.isAuthenticated && session.userDetails.ZCR_WEBPASSCHNGFLAG !== "Y") {
        handleLogin(session);
        return;
      }

      // Another tab only refreshed the access token - use the newest one here too
      if (userStore.isAuthenticated && userStore.userDetails.accessToken !== accessToken) {
        userStore.setUserDetails({ ...userStore.userDetails, accessToken });
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [navigate]);
};
